import {
  BUTTON_HEIGHT,
  FONT_SIZE,
  FONT_SIZE_VALUE,
  ROUTES,
  VIEW_LIST_MODE_OPTIONS,
} from "@lhd/constants";
import {
  useFetchWorkerLogtimePermissions,
  useFetchWorkerLogtimesMonthlyV2,
  useSaveWorkerLogtimesBulkV2,
} from "@lhd/hooks";
import type {
  WorkerLogtimeBulkSavePayload,
  WorkerLogtimeBulkSaveRecord,
  WorkerLogtimeBulkSaveValue,
  WorkerLogtimeCorrectedValue,
  WorkerLogtimeMonthlyV2Params,
} from "@lhd/hooks";
import { getCompanyId } from "@lhd/utility";
import moment, { Moment } from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAttendanceManagementPermission } from "../useAttendanceManagementPermission";
import {
  workerLogtimesMonthlyParamsFromUrl,
  clearMonthlyListFilters,
} from "./monthlyListQuery";
import { useSelector } from "react-redux";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { DISPLAY_SIZES } from "@lhd/types";
import { parsePositiveIntWithDefault } from "../until";
import { useControlStatement } from "@lhd/hooks";
import { MODE } from "@lhd/types";
import type { TableFormRef } from "@lhd/components/TableForm";
import { workDateTimeToLoggedAtIso } from "../workLogTimezone";
import { notification } from "antd";
import usePrintPdf from "./usePrintPdf";
export type ReceivedProps = Record<string, any>;

const parsePositiveInt = (raw: string | null): number | undefined => {
  if (raw == null || raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const useListTracking = (props: ReceivedProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = getCompanyId();
  const {
    canManageWorkersAttendance,
    isSuperAdmin,
    currentWorkerId,
    companies,
    isAdmin,
  } = useAttendanceManagementPermission();
  const displaySize =
    (useSelector(getDisplaySize) as
      | keyof typeof FONT_SIZE
      | keyof typeof BUTTON_HEIGHT) || DISPLAY_SIZES.SMALL;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];

  const tableFormRef = useRef<TableFormRef>(null);
  const saveInFlightRef = useRef(false);
  const [stateForm, setStateForm] = useState({
    hasChanged: false,
    hasError: false,
    hasUnsavedChanges: false,
  });

  const discardEditChanges = useCallback(() => {
    tableFormRef.current?.discardChanges();
    setStateForm({
      hasChanged: false,
      hasError: false,
      hasUnsavedChanges: false,
    });
  }, []);

  const { data: logtimePermissions, isLoading: isLoadingLogtimePermissions } =
    useFetchWorkerLogtimePermissions(!isAdmin);

  const hasPermissionAddNew =
    canManageWorkersAttendance ||
    !!logtimePermissions?.logtime_create_delete.length;

  const year = searchParams.get("year") ?? moment().format("YYYY");
  const month = searchParams.get("month") ?? moment().format("MM");
  const page = parsePositiveIntWithDefault(searchParams.get("page"), 1);
  const perPage = parsePositiveIntWithDefault(searchParams.get("per_page"), 50);
  const hasEmptyFilter = searchParams.get("empty_filter") === "1";

  const { btnPrint, elementPrint } = usePrintPdf({
    displaySize,
    companyId,
    year: Number(year),
    month: Number(month),
  });

  useEffect(() => {
    if (canManageWorkersAttendance || currentWorkerId == null) return;
    const wid = parsePositiveInt(searchParams.get("worker_id"));
    if (wid != null) return;
    const next = new URLSearchParams(searchParams);
    next.set("worker_id", String(currentWorkerId));
    setSearchParams(next, { replace: true });
  }, [
    canManageWorkersAttendance,
    currentWorkerId,
    searchParams,
    setSearchParams,
  ]);

  const monthlyParams = useMemo<WorkerLogtimeMonthlyV2Params | null>(() => {
    if (!companyId) return null;
    if (!canManageWorkersAttendance && currentWorkerId == null) return null;
    if (hasEmptyFilter) return null;

    const base = {
      year,
      month,
      page,
      perPage,
      isWithoutWorker: true,
    };
    const paramsFromUrl = workerLogtimesMonthlyParamsFromUrl(
      base,
      searchParams,
    );

    return {
      company_id: companyId,
      year: Number(paramsFromUrl.year),
      month: Number(paramsFromUrl.month),
      page: paramsFromUrl.page,
      per_page: paramsFromUrl.perPage,
      ...(paramsFromUrl.sort ? { sort: paramsFromUrl.sort } : {}),
      ...(paramsFromUrl.filters ? { filters: paramsFromUrl.filters } : {}),
    };
  }, [
    companyId,
    year,
    month,
    page,
    perPage,
    hasEmptyFilter,
    canManageWorkersAttendance,
    currentWorkerId,
    searchParams,
  ]);

  const {
    data: monthlyResult,
    isLoading,
    isFetching,
    refetch: refetchMonthly,
  } = useFetchWorkerLogtimesMonthlyV2(
    monthlyParams ?? {},
    monthlyParams !== null,
  );

  const monthlyRecords = hasEmptyFilter ? [] : monthlyResult?.data ?? [];
  const monthlyMaxBreakPairs = monthlyResult?.meta.max_break_pairs ?? 0;
  const monthlyMeta = useMemo(() => {
    if (hasEmptyFilter) {
      return {
        currentPage: page,
        perPage,
        totalRecords: 0,
        totalPages: 0,
      };
    }
    if (!monthlyResult?.pagination) return undefined;

    return {
      currentPage: monthlyResult.pagination.current_page,
      perPage: monthlyResult.pagination.per_page,
      totalRecords: monthlyResult.pagination.total_records,
      totalPages: monthlyResult.pagination.total_pages,
    };
  }, [hasEmptyFilter, monthlyResult?.pagination, page, perPage]);

  useEffect(() => {
    if (isLoading || isFetching || !monthlyMeta) return;

    const totalPages = Math.max(monthlyMeta.totalPages, 1);
    const isPageOutOfRange = page > totalPages;
    const isEmptyPageWithDataElsewhere =
      page > 1 && monthlyRecords.length === 0 && monthlyMeta.totalRecords > 0;

    if (!isPageOutOfRange && !isEmptyPageWithDataElsewhere) return;

    const next = new URLSearchParams(searchParams);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  }, [
    isFetching,
    isLoading,
    monthlyMeta,
    monthlyRecords.length,
    page,
    searchParams,
    setSearchParams,
  ]);

  const monthPickerValue = useMemo(
    () => moment(`${year}-${month.padStart(2, "0")}`, "YYYY-MM"),
    [year, month],
  );

  const onMonthChange = useCallback(
    (date: Moment | null) => {
      if (!date) return;
      const next = clearMonthlyListFilters(new URLSearchParams(searchParams));
      next.delete("empty_filter");
      next.set("year", date.format("YYYY"));
      next.set("month", date.format("MM"));
      next.set("page", "1");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const onChangePagination = useCallback(
    (nextPage: number, nextPageSize?: number) => {
      const next = new URLSearchParams(searchParams);
      if (nextPageSize && nextPageSize !== perPage) {
        next.set("per_page", String(nextPageSize));
        next.set("page", "1");
      } else {
        next.set("page", String(nextPage));
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams, perPage],
  );

  const handleBackToHome = useCallback(() => {
    navigate(ROUTES.TRACKING_TIME);
  }, [navigate]);

  const handleAttendanceSaved = useCallback(async () => {
    await refetchMonthly();
  }, [refetchMonthly]);
  const { mutate: saveWorkerLogtimes, isLoading: isSaving } =
    useSaveWorkerLogtimesBulkV2();

  const switchModeOptions = useMemo(() => {
    const hasCanEdit = monthlyRecords.some(
      (x) => x.worker_id === currentWorkerId || x.can_edit || x.can_delete,
    );
    return VIEW_LIST_MODE_OPTIONS.map((x) => {
      let disabled = x.disabled;
      if (x.value === MODE.EDIT) {
        disabled = !hasCanEdit && !isAdmin;
      }
      return {
        ...x,
        disabled,
      };
    });
  }, [currentWorkerId, monthlyRecords, isAdmin]);

  const {
    elementSegmentedControl,
    value: pageMode,
    setValue: setPageMode,
  } = useControlStatement({
    options: switchModeOptions,
    displaySize,
    defaultValue: MODE.VIEW,
    listModeDevelop: [MODE.EDIT_LAYOUT],
    showPopup: stateForm.hasUnsavedChanges,
    onChange: (nextValue) => {
      if (nextValue !== MODE.EDIT) discardEditChanges();
    },
  });

  const isEditMode = pageMode === MODE.EDIT;
  const handleSave = useCallback(() => {
    if (
      saveInFlightRef.current ||
      !stateForm.hasChanged ||
      stateForm.hasError ||
      isSaving
    ) {
      return;
    }
    const dataChanged = tableFormRef.current?.getChangedData() ?? {};
    const recordsByRowId = new Map(
      monthlyRecords.map((record) => [
        `${record.worker_id}-${record.date}`,
        record,
      ]),
    );
    const hasChangedField = (changes: Record<string, any>, field: string) =>
      Object.prototype.hasOwnProperty.call(changes, field);
    const toLogtimeValue = (
      value: unknown,
      existingValue: WorkerLogtimeCorrectedValue | null | undefined,
      date: string,
    ): WorkerLogtimeBulkSaveValue | undefined => {
      const time = String(value ?? "").trim();

      if (!time) {
        if (!existingValue) return undefined;
        return {
          id: existingValue.id,
          _destroy: true,
          updated_at: existingValue.updated_at,
        };
      }

      const loggedAt = workDateTimeToLoggedAtIso(date, time);
      if (!loggedAt) return undefined;

      return existingValue
        ? {
            id: existingValue.id,
            logged_at: loggedAt,
            updated_at: existingValue.updated_at,
          }
        : { logged_at: loggedAt };
    };

    const records = Object.entries(dataChanged).reduce<
      WorkerLogtimeBulkSaveRecord[]
    >((result, [rowId, changes]) => {
      const source = recordsByRowId.get(rowId);
      if (!source) return result;

      const record: WorkerLogtimeBulkSaveRecord = {
        worker_id: source.worker_id,
        date: source.date,
      };

      if (hasChangedField(changes, "correctedCheckIn")) {
        const checkIn = toLogtimeValue(
          changes.correctedCheckIn,
          source.corrected.check_in,
          source.date,
        );
        if (checkIn) record.check_in = checkIn;
      }
      if (hasChangedField(changes, "correctedCheckOut")) {
        const checkOut = toLogtimeValue(
          changes.correctedCheckOut,
          source.corrected.check_out,
          source.date,
        );
        if (checkOut) record.check_out = checkOut;
      }

      const changedBreakIndexes = Object.keys(changes)
        .map((field) => field.match(/^correctedBreak(?:Start|End)(\d+)$/))
        .filter((match): match is RegExpMatchArray => match !== null)
        .map((match) => Number(match[1]));
      const maxChangedBreakIndex = changedBreakIndexes.length
        ? Math.max(...changedBreakIndexes)
        : -1;

      if (maxChangedBreakIndex >= 0) {
        const breaks = Array.from(
          { length: maxChangedBreakIndex + 1 },
          (_, breakIndex) => {
            const startField = `correctedBreakStart${breakIndex}`;
            const endField = `correctedBreakEnd${breakIndex}`;
            const breakPayload: NonNullable<
              WorkerLogtimeBulkSaveRecord["breaks"]
            >[number] = {};

            if (hasChangedField(changes, startField)) {
              const start = toLogtimeValue(
                changes[startField],
                source.corrected.breaks?.[breakIndex]?.start,
                source.date,
              );
              if (start) breakPayload.start = start;
            }
            if (hasChangedField(changes, endField)) {
              const end = toLogtimeValue(
                changes[endField],
                source.corrected.breaks?.[breakIndex]?.end,
                source.date,
              );
              if (end) breakPayload.end = end;
            }

            return breakPayload;
          },
        );
        if (breaks.some((breakPayload) => Object.keys(breakPayload).length)) {
          record.breaks = breaks;
        }
      }

      if (hasChangedField(changes, "memo")) {
        record.note = String(changes.memo ?? "");
        if (source.note_id != null) record.note_id = source.note_id;
        if (source.note_updated_at != null) {
          record.note_updated_at = source.note_updated_at;
        }
      }

      if (Object.keys(record).length > 2) result.push(record);
      return result;
    }, []);

    const payload: WorkerLogtimeBulkSavePayload = {
      company_id: companyId,
      records,
    };

    if (!payload.records.length) return;

    saveInFlightRef.current = true;
    saveWorkerLogtimes(payload, {
      onSuccess: async (response) => {
        try {
          if (response.failed_count > 0) {
            const firstError = response.results.find(
              (result) => result.status === "error",
            )?.errors?.[0];
            notification.error({
              message:
                firstError?.message || "一部のデータを保存できませんでした。",
            });
            return;
          }

          await refetchMonthly();
          notification.success({ message: "保存しました。" });
          setPageMode(MODE.VIEW);
          discardEditChanges();
        } finally {
          saveInFlightRef.current = false;
        }
      },
      onError: () => {
        saveInFlightRef.current = false;
        notification.error({ message: "保存に失敗しました。" });
      },
    });
  }, [
    companyId,
    discardEditChanges,
    isSaving,
    monthlyRecords,
    refetchMonthly,
    saveWorkerLogtimes,
    setPageMode,
    stateForm.hasChanged,
    stateForm.hasError,
  ]);
  const handleCancelEdit = useCallback(() => {
    discardEditChanges();
    setPageMode(MODE.VIEW);
  }, [discardEditChanges, setPageMode]);

  return {
    ...props,
    companyId,
    year,
    month,
    page,
    perPage,
    monthPickerValue,
    onMonthChange,
    onChangePagination,
    monthlyRecords,
    monthlyMaxBreakPairs,
    monthlyMeta,
    listLoading: monthlyParams === null ? false : isLoading || isFetching,
    canManageWorkersAttendance,
    isSuperAdmin,
    companies,
    handleBackToHome,
    handleAttendanceSaved,
    displaySize,
    height,
    size,
    hasPermissionAddNew,
    elementSegmentedControl,
    pageMode,
    tableFormRef,
    handleSetStateForm: setStateForm,
    hasChanged: stateForm.hasChanged,
    isError: stateForm.hasError,
    isEditMode,
    handleSave,
    handleCancelEdit,
    isSaving,
    monthlyResult: hasEmptyFilter ? undefined : monthlyResult,
    btnPrint,
    elementPrint,
  };
};

export type Props = ReturnType<typeof useListTracking>;

export default useListTracking;
